import { formatNumber } from "@/utils";
import { getLocalisedName } from "@/utils/i18n";

import { SEARCHABLE_COMMODITY_FIELDS } from "./constants";
import {
  type CommodityDashboardStat,
  type CommodityListItem,
  type CountryDashboardData,
  type EntityId,
  type QuantityByUnit,
  type UnitOfMeasure,
} from "./types";

const normalizeSearchValue = (value: string) => value.trim().toLocaleLowerCase();

// Matches the English name as well as the displayed localised name, so searching
// works against whichever name the user can actually see.
export const filterCommodities = (
  commodities: CommodityListItem[],
  searchTerm?: string,
  languageCode?: string
) => {
  const query = normalizeSearchValue(searchTerm ?? "");

  if (!query) {
    return commodities;
  }

  return commodities.filter(commodity => {
    const searchableValues = SEARCHABLE_COMMODITY_FIELDS.map(field => commodity[field]);

    if (languageCode) {
      searchableValues.push(getCommodityTranslation(commodity, languageCode));
    }

    return searchableValues.some(value =>
      normalizeSearchValue(String(value ?? "")).includes(query)
    );
  });
};

export const getCommodityRowId = (row: CommodityListItem) => String(row.id);

export const buildCommodityStatsMap = (countryDashboardData?: CountryDashboardData) => {
  const stats = countryDashboardData?.commodities_stats ?? [];

  return new Map<EntityId, CommodityDashboardStat>(
    stats
      .filter(
        (stat): stat is CommodityDashboardStat & { commodity_id: EntityId } =>
          stat.commodity_id !== undefined
      )
      .map(stat => [stat.commodity_id, stat])
  );
};

export const getFarmersWithCommodity = (stat?: CommodityDashboardStat) =>
  stat?.farmers_with_commodity_count ?? 0;

export const getLandWithCommoditySum = (stat?: CommodityDashboardStat) =>
  stat?.land_with_commodity_sum ?? 0;

export const getDepositsCount = (stat?: CommodityDashboardStat) => stat?.deposits_count ?? 0;

export const getHarvestsCount = (stat?: CommodityDashboardStat) => stat?.harvests_count ?? 0;

export const getEventsCount = (stat?: CommodityDashboardStat) => stat?.events_count ?? 0;

// Average land size per farmer for the commodity, guarding division by zero.
export const getAverageLandSize = (stat?: CommodityDashboardStat) => {
  const farmers = stat?.farmers_with_commodity_count ?? 0;

  return farmers > 0 ? (stat?.land_with_commodity_sum ?? 0) / farmers : 0;
};

// Groups per-unit quantities by unit code and renders them as
// "1,200.55 kg | 235 bag": thousands-separated numbers (max 2 decimals) with a
// space before the unit code, joined by " | ". Returns "0" when there is nothing.
export const formatQuantityByUnit = (
  entries: QuantityByUnit[] | undefined,
  unitsOfMeasure: UnitOfMeasure[]
) => {
  const list = entries ?? [];

  if (list.length === 0) {
    return "0";
  }

  const quantitiesByUnit = list.reduce<Record<string, number>>((acc, entry) => {
    const unitOfMeasure = unitsOfMeasure.find(
      unit => unit.id === parseInt(String(entry.unit), 10)
    ) ?? { code: "---" };
    const code = unitOfMeasure.code ?? "---";

    return {
      ...acc,
      [code]: (acc[code] ?? 0) + Number(entry.quantity),
    };
  }, {});

  return Object.keys(quantitiesByUnit)
    .map(code => `${formatNumber(quantitiesByUnit[code])} ${code}`)
    .join(" | ");
};

export const getQuantityDepositedLabel = (
  stat: CommodityDashboardStat | undefined,
  unitsOfMeasure: UnitOfMeasure[]
) => formatQuantityByUnit(stat?.deposits_with_commodity_quantity_sum_full, unitsOfMeasure);

export const getQuantityHarvestedLabel = (
  stat: CommodityDashboardStat | undefined,
  unitsOfMeasure: UnitOfMeasure[]
) => formatQuantityByUnit(stat?.harvests_quantity_sum_full, unitsOfMeasure);

export const getQuantitySoldLabel = (
  stat: CommodityDashboardStat | undefined,
  unitsOfMeasure: UnitOfMeasure[]
) => formatQuantityByUnit(stat?.sold_quantity_sum_full, unitsOfMeasure);

// Total quantity across every unit, used purely to give the multi-unit quantity columns
// a numeric sort key (mixing units is acceptable for ordering, not for display).
export const sumQuantityByUnit = (entries?: QuantityByUnit[]) =>
  (entries ?? []).reduce((total, entry) => total + Number(entry.quantity), 0);

export const getQuantityDepositedTotal = (stat?: CommodityDashboardStat) =>
  sumQuantityByUnit(stat?.deposits_with_commodity_quantity_sum_full);

export const getQuantityHarvestedTotal = (stat?: CommodityDashboardStat) =>
  sumQuantityByUnit(stat?.harvests_quantity_sum_full);

export const getQuantitySoldTotal = (stat?: CommodityDashboardStat) =>
  sumQuantityByUnit(stat?.sold_quantity_sum_full);

export const getLandUnitLabel = (countryDashboardData?: CountryDashboardData) =>
  getLocalisedName(countryDashboardData?.country_info?.land_unit_of_measurement) as
    | string
    | undefined;

export const isCommodityAvailableToCountry = (commodity: CommodityListItem, countryId?: EntityId) =>
  countryId !== undefined && commodity.countries.includes(countryId);

// Falls back to the commodity's default name when untranslated for the given language.
export const getCommodityTranslation = (commodity: CommodityListItem, languageCode: string) =>
  commodity.name_translations[languageCode] || commodity.name;
